import { createSlice } from '@reduxjs/toolkit'
import { pull, uniq } from 'lodash'

const initialState = {
  loading: false,
  error: false,
  user: null,
  typeLoading: [],
  company: {},
  savingsTotals: {},
  selectedUsers: [],
  storedUsers: [],
  hrisAccounts: null,
  hrisStatus: {},
  firstSyncStatus: {},
  plaidLinkToken: {},
  insuranceDisabled: true,
  destinationImages: {},
  destinationsToIgnore: [],
  travelLoyalties: {},
  locationsSearchResults: [],
  questionTimeline: [],
}

const globalSlice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    request: (state, action) => {
      state.loading = true
      state.error = false
      state[`error${action.payload.typeLoading}`] = null
      state.data = null
      if (!state.typeLoading.includes(action.payload.typeLoading)) {
        state.typeLoading.push(action.payload.typeLoading)
      }
    },
    success: (state, action) => {
      const temp = { ...action.payload }
      delete temp.typeLoading
      Object.assign(state, temp)
      state.loading = false
      state.error = false
      if (+action.payload?.code - 200) state[`statusCode${action.payload?.typeLoading}`] = +action.payload?.code - 200
      pull(state.typeLoading, action.payload?.typeLoading)
    },
    error: (state, action) => {
      state.loading = false
      state.error = action.payload.error
      if (action.payload.error) state[`error${action.payload?.typeLoading}`] = action.payload.error
      state.data = null
      pull(state.typeLoading, action.payload?.typeLoading)
    },
    clearFromState: (state, action) => {
      action.payload?.forEach?.(o => {
        state[o] = null
      })
    },
    resetState: (state, action) => {
      action.payload?.forEach?.(o => {
        state[o] = initialState[o]
      })
    },
    setToken: (state, action) => {
      state.token = action.payload.token
      state.tokenExpiry = action.payload.tokenExpiry
    },
    addEmptyTravelLoyalty: (state, action) => {
      const loyalties = state.travelLoyalties?.[action.payload.program_type_name] || []
      if (!loyalties?.[Math.max(loyalties.length - 1, 0)]?.temp) {
        state.travelLoyalties[action.payload.program_type_name] = [
          ...loyalties,
          { temp: true }
        ]
      }
    },
    setSelectedUsers: (state, action) => {
      state.selectedUsers = action.payload
      state.clearedUnverified = false
      state.clearedVerified = false
    },
    addSelectedUser: (state, action) => {
      const uniqueId = action.payload?.unique_id
      if (!state.selectedUsers.includes(uniqueId)) {
        state.selectedUsers.push(uniqueId)
        state.clearedUnverified = false
        state.clearedVerified = false
      }
    },
    removeSelectedUser: (state, action) => {
      const uniqueId = action.payload.unique_id
      state.selectedUsers = state.selectedUsers.filter(userId => userId !== uniqueId)
      state.clearedUnverified = false
      state.clearedVerified = false
    },
    clearUnverifiedUsers: (state, action) => {
      const isPoints = [1, 2].includes(state.company?.currency_config)
      const employees = state.storedUsers || []
      // const employees = state[action.payload.selector || 'employees'] || []
      const filteredUsers = employees.filter(user => {
        return state.selectedUsers.includes(user.unique_id) && (isPoints ? !!user.last_login : (user.has_savings_account || !user.is_wallet_capable))
      }).map(user => user.unique_id)

      state.selectedUsers = uniq(filteredUsers)
      state.clearedUnverified = state.selectedUsers.length !== filteredUsers.length
    },
    clearVerifiedUsers: (state, action) => {
      const employees = state.storedUsers || []
      // const employees = state[action.payload.selector || 'employees'] || []
      const filteredUsers = employees.filter(user => state.selectedUsers.includes(user.unique_id) && !user.has_savings_account).map(user => user.unique_id)

      state.selectedUsers = uniq(filteredUsers)
      state.clearedVerified = state.selectedUsers.length !== filteredUsers.length
    },
    removeSelfFromSelectedUsers: (state) => {
      const uniqueId = state.user.unique_id
      state.selectedUsers = state.selectedUsers.filter(userId => userId !== uniqueId)
    },
    clearBlob: state => {
      state.blobDownload = undefined
    },
    setAuthUser: (state, action) => {
      state.authUser = action.payload
    },
    setBookingData: (state, action) => {
      state.bookingData = { ...state.bookingData, ...action.payload }
    },
    toggleHrisReview: (state, action) => {
      state.hrisReviewOpen = action.payload
    },
    addToStoredUsers: (state, action) => {
      state.storedUsers.push(action.payload)
    },
    toggleAffiliateFavorite: (state, action) => {
      const affiliate = state.affiliates.find(affiliate => affiliate.unique_id === action.payload.unique_id)
      if (affiliate) {
        affiliate.favorited = action.payload.newValue
      }
    },
    addToQuestionTimeline: (state, action) => {
      state.questionTimeline.push(action.payload)
    },
    clearQuestionTimeline: (state, action) => {
      state.questionTimeline = []
    },
  },
})

export const {
  request,
  success,
  error,
  clearFromState,
  resetState,
  setToken,
  addEmptyTravelLoyalty,
  setSelectedUsers,
  addSelectedUser,
  removeSelectedUser,
  clearUnverifiedUsers,
  clearVerifiedUsers,
  removeSelfFromSelectedUsers,
  clearBlob,
  setAuthUser,
  setBookingData,
  toggleHrisReview,
  addToStoredUsers,
  toggleAffiliateFavorite,
  addToQuestionTimeline,
  clearQuestionTimeline,
} = globalSlice.actions

export default globalSlice.reducer
